/**
 * Error Messages Constants
 * Centralized error message mapping for user-friendly error display
 */
export const ERROR_MESSAGES = {
    // General errors
    NETWORK_ERROR: 'Network error. Please check your connection and try again.',
    SERVER_ERROR: 'Server error. Please try again later.',
    /** Gateway / upstream unavailable (502, 503, 504) — prefer over raw status codes */
    SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again in a few moments.',
    RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
    REQUEST_TIMEOUT: 'The request timed out. Please try again.',
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    FORBIDDEN: 'Access denied. You do not have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    // Quiz template errors
    QUIZ_TEMPLATE: {
        TITLE_REQUIRED: 'Quiz title is required.',
        TITLE_TOO_LONG: 'Quiz title must be less than 255 characters.',
        DESCRIPTION_TOO_LONG: 'Description must be less than 2000 characters.',
        NO_QUESTIONS: 'At least one question is required.',
        TOO_MANY_QUESTIONS: 'Maximum 50 questions allowed.',
        INVALID_QUESTION_TYPE: 'Invalid question type selected.',
        INVALID_DIFFICULTY: 'Invalid difficulty level selected.',
        TIMER_INVALID: 'Timer must be between 1 and 480 minutes.',
        SUBTOPIC_INVALID: 'Invalid subtopic format.',
        FILE_TOO_LARGE: 'File size exceeds 10MB limit.',
        FILE_TYPE_INVALID: 'Only PDF and TXT files are allowed.',
        FILE_UPLOAD_FAILED: 'Failed to upload file. Please try again.',
        FILE_PARSE_FAILED: 'Failed to parse file content.',
        TEMPLATE_CREATE_FAILED: 'Failed to create quiz template.',
        TEMPLATE_UPDATE_FAILED: 'Failed to update quiz template.',
        TEMPLATE_DELETE_FAILED: 'Failed to delete quiz template.',
        TEMPLATE_NOT_FOUND: 'Quiz template not found.',
        TEMPLATE_ACCESS_DENIED: 'You do not have access to this quiz template.'
    },
    // Question errors
    QUESTION: {
        TEXT_REQUIRED: 'Question text is required.',
        TEXT_TOO_LONG: 'Question text must be less than 1000 characters.',
        ANSWER_REQUIRED: 'Answer is required.',
        ANSWER_TOO_LONG: 'Answer must be less than 500 characters.',
        MULTIPLE_CHOICE_ANSWERS_REQUIRED: 'All multiple choice answers (A, B, C) are required.',
        CORRECT_ANSWER_REQUIRED: 'Correct answer must be selected.',
        SCENARIO_REQUIRED: 'Scenario text is required for scenario-based questions.',
        SCENARIO_TOO_LONG: 'Scenario text must be less than 1000 characters.',
        INVALID_DIFFICULTY: 'Invalid difficulty level.',
        POINTS_INVALID: 'Points must be between 1 and 10.'
    },
    // File upload errors
    FILE_UPLOAD: {
        NO_FILE: 'No file selected.',
        FILE_TOO_LARGE: 'File size exceeds the maximum limit.',
        INVALID_TYPE: 'Invalid file type. Only PDF and TXT files are allowed.',
        UPLOAD_FAILED: 'File upload failed. Please try again.',
        PARSE_FAILED: 'Failed to parse file content.',
        NETWORK_ERROR: 'Network error during upload. Please try again.',
        SERVER_ERROR: 'Server error during upload. Please try again.'
    },
    // Form validation errors
    FORM: {
        REQUIRED_FIELD: 'This field is required.',
        INVALID_EMAIL: 'Please enter a valid email address.',
        INVALID_URL: 'Please enter a valid URL.',
        PASSWORD_TOO_SHORT: 'Password must be at least 8 characters.',
        PASSWORD_MISMATCH: 'Passwords do not match.',
        INVALID_DATE: 'Please enter a valid date.',
        INVALID_NUMBER: 'Please enter a valid number.',
        NUMBER_TOO_SMALL: 'Number is too small.',
        NUMBER_TOO_LARGE: 'Number is too large.',
        INVALID_FORMAT: 'Invalid format.'
    },
    // Authentication errors
    AUTH: {
        LOGIN_FAILED: 'Login failed. Please check your credentials.',
        LOGOUT_FAILED: 'Logout failed. Please try again.',
        SESSION_EXPIRED: 'Your session has expired. Please log in again.',
        INVALID_TOKEN: 'Invalid authentication token.',
        ACCOUNT_LOCKED: 'Your account has been locked. Please contact support.',
        EMAIL_NOT_VERIFIED: 'Please verify your email address before continuing.'
    },
    // Subscription errors
    SUBSCRIPTION: {
        REQUIRED: 'An active subscription is required to access this feature. Please subscribe to continue.',
        EXPIRED: 'Your subscription has expired. Please renew to continue using this feature.',
        NOT_FOUND: 'No active subscription found. Please subscribe to access this feature.',
        UPGRADE_REQUIRED: 'This feature requires a higher subscription tier. Please upgrade to continue.',
        PAYMENT_FAILED: 'Payment processing failed. Please check your payment method and try again.',
        CREATION_FAILED: 'Failed to create subscription. Please try again or contact support.',
        VALIDATION_ERROR: 'Subscription information is invalid. Please check your details and try again.'
    },
    // Classroom errors
    CLASSROOM: {
        NAME_REQUIRED: 'Classroom name is required.',
        NAME_TOO_LONG: 'Classroom name must be less than 255 characters.',
        DESCRIPTION_TOO_LONG: 'Description must be less than 1000 characters.',
        INVALID_GRADE_LEVEL: 'Invalid grade level selected.',
        INVALID_SUBJECT: 'Invalid subject selected.',
        CLASSROOM_NOT_FOUND: 'Classroom not found.',
        ACCESS_DENIED: 'You do not have access to this classroom.',
        STUDENT_ALREADY_ADDED: 'Student is already in this classroom.',
        STUDENT_NOT_FOUND: 'Student not found.',
        QUIZ_ALREADY_ASSIGNED: 'Quiz is already assigned to this classroom.'
    }
};
// Error code to message mapping
export const ERROR_CODE_MAPPING = {
    // HTTP status codes
    '400': ERROR_MESSAGES.VALIDATION_ERROR,
    '401': ERROR_MESSAGES.UNAUTHORIZED,
    '403': ERROR_MESSAGES.FORBIDDEN,
    '404': ERROR_MESSAGES.NOT_FOUND,
    '500': ERROR_MESSAGES.SERVER_ERROR,
    '502': ERROR_MESSAGES.SERVER_ERROR,
    '503': ERROR_MESSAGES.SERVER_ERROR,
    // Custom error codes
    'QUIZ_TEMPLATE_NOT_FOUND': ERROR_MESSAGES.QUIZ_TEMPLATE.TEMPLATE_NOT_FOUND,
    'QUIZ_TEMPLATE_ACCESS_DENIED': ERROR_MESSAGES.QUIZ_TEMPLATE.TEMPLATE_ACCESS_DENIED,
    'FILE_UPLOAD_FAILED': ERROR_MESSAGES.FILE_UPLOAD.UPLOAD_FAILED,
    'FILE_PARSE_FAILED': ERROR_MESSAGES.FILE_UPLOAD.PARSE_FAILED,
    'INVALID_QUESTION_TYPE': ERROR_MESSAGES.QUIZ_TEMPLATE.INVALID_QUESTION_TYPE,
    'NO_QUESTIONS': ERROR_MESSAGES.QUIZ_TEMPLATE.NO_QUESTIONS,
    'TITLE_REQUIRED': ERROR_MESSAGES.QUIZ_TEMPLATE.TITLE_REQUIRED,
    'TITLE_TOO_LONG': ERROR_MESSAGES.QUIZ_TEMPLATE.TITLE_TOO_LONG,
    'DESCRIPTION_TOO_LONG': ERROR_MESSAGES.QUIZ_TEMPLATE.DESCRIPTION_TOO_LONG,
    'TIMER_INVALID': ERROR_MESSAGES.QUIZ_TEMPLATE.TIMER_INVALID,
    'FILE_TOO_LARGE': ERROR_MESSAGES.FILE_UPLOAD.FILE_TOO_LARGE,
    'INVALID_FILE_TYPE': ERROR_MESSAGES.FILE_UPLOAD.INVALID_TYPE,
    // Subscription error codes
    'SUBSCRIPTION_REQUIRED': ERROR_MESSAGES.SUBSCRIPTION.REQUIRED,
    'SUBSCRIPTION_EXPIRED': ERROR_MESSAGES.SUBSCRIPTION.EXPIRED,
    'SUBSCRIPTION_NOT_FOUND': ERROR_MESSAGES.SUBSCRIPTION.NOT_FOUND,
    'UPGRADE_REQUIRED': ERROR_MESSAGES.SUBSCRIPTION.UPGRADE_REQUIRED,
    'PAYMENT_FAILED': ERROR_MESSAGES.SUBSCRIPTION.PAYMENT_FAILED,
    'SUBSCRIPTION_CREATION_FAILED': ERROR_MESSAGES.SUBSCRIPTION.CREATION_FAILED
};
// Function to get user-friendly error message
export const getErrorMessage = (error) => {
    // Handle different error formats
    if (typeof error === 'string') {
        return ERROR_CODE_MAPPING[error] || error;
    }
    if (error?.code) {
        return ERROR_CODE_MAPPING[error.code] || error.message || ERROR_MESSAGES.SERVER_ERROR;
    }
    if (error?.message) {
        // Check if it's a known error message
        const knownMessage = Object.values(ERROR_MESSAGES).find(msg => Object.values(msg).includes(error.message));
        if (knownMessage) {
            return error.message;
        }
        // Check error code mapping
        return ERROR_CODE_MAPPING[error.message] || error.message;
    }
    if (error?.response?.data?.message) {
        return ERROR_CODE_MAPPING[error.response.data.message] || error.response.data.message;
    }
    if (error?.response?.status) {
        return ERROR_CODE_MAPPING[error.response.status.toString()] || ERROR_MESSAGES.SERVER_ERROR;
    }
    return ERROR_MESSAGES.SERVER_ERROR;
};
// Function to get field-specific error message
export const getFieldErrorMessage = (field, error) => {
    const fieldErrors = {
        title: ERROR_MESSAGES.QUIZ_TEMPLATE,
        description: ERROR_MESSAGES.QUIZ_TEMPLATE,
        type: ERROR_MESSAGES.QUIZ_TEMPLATE,
        difficulty: ERROR_MESSAGES.QUIZ_TEMPLATE,
        timer_minutes: ERROR_MESSAGES.QUIZ_TEMPLATE,
        file: ERROR_MESSAGES.FILE_UPLOAD,
        question: ERROR_MESSAGES.QUESTION,
        answerA: ERROR_MESSAGES.QUESTION,
        answerB: ERROR_MESSAGES.QUESTION,
        answerC: ERROR_MESSAGES.QUESTION,
        correctAnswer: ERROR_MESSAGES.QUESTION,
        scenario: ERROR_MESSAGES.QUESTION
    };
    const fieldErrorMap = fieldErrors[field];
    if (fieldErrorMap) {
        const errorKey = Object.keys(fieldErrorMap).find(key => error.message?.includes(key) || error.code === key);
        if (errorKey) {
            return fieldErrorMap[errorKey];
        }
    }
    return getErrorMessage(error);
};
export default {
    ERROR_MESSAGES,
    ERROR_CODE_MAPPING,
    getErrorMessage,
    getFieldErrorMessage
};
